import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  ArrowLeft, 
  Search, 
  Star, 
  Clock, 
  MapPin, 
  Phone,
  Video,
  Building2,
  GraduationCap,
  Languages,
  Calendar,
  Loader2,
  User,
  Stethoscope
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConsultDoctor = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const provisionalDiagnosis = searchParams.get("diagnosis") || "";
  
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSpecialty, setSelectedSpecialty] = useState("all");
  const [consultationType, setConsultationType] = useState("in_person");
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [booking, setBooking] = useState(false);
  
  const [bookingForm, setBookingForm] = useState({
    name: "",
    age: "",
    sex: "Male",
    phone: "",
    preferred_slot: "",
    complaints: provisionalDiagnosis || ""
  });

  useEffect(() => {
    fetchDoctors();
  }, [consultationType]);

  const fetchDoctors = async () => {
    try {
      setLoading(true);
      const params = consultationType === "teleconsult" ? "teleconsult=true" : "";
      const response = await axios.get(`${API}/doctors?${params}`, { withCredentials: true });
      setDoctors(response.data);
    } catch (error) {
      console.error("Failed to fetch doctors:", error);
      toast.error("Failed to load doctors");
    } finally {
      setLoading(false);
    }
  };

  const specialties = [...new Set(doctors.map(d => d.specialty))];

  const filteredDoctors = doctors.filter(doctor => {
    const matchesSearch = 
      doctor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doctor.specialty.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doctor.hospital.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSpecialty = selectedSpecialty === "all" || doctor.specialty === selectedSpecialty;
    return matchesSearch && matchesSpecialty;
  });

  const handleBookAppointment = (doctor) => {
    setSelectedDoctor(doctor);
    setShowBookingModal(true);
  };

  const handleSubmitBooking = async () => {
    if (!bookingForm.name || !bookingForm.age || !bookingForm.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setBooking(true);
    try {
      await axios.post(`${API}/booking`, {
        doctor_id: selectedDoctor.id,
        patient_name: bookingForm.name,
        age: parseInt(bookingForm.age),
        sex: bookingForm.sex,
        phone: bookingForm.phone,
        complaints: bookingForm.complaints,
        provisional_diagnosis: provisionalDiagnosis,
        consultation_type: consultationType,
        preferred_slot: bookingForm.preferred_slot
      }, { withCredentials: true });
      
      toast.success("Appointment booked successfully!");
      setShowBookingModal(false);
      setBookingForm({ name: "", age: "", sex: "Male", phone: "", preferred_slot: "", complaints: provisionalDiagnosis || "" });
    } catch (error) {
      toast.error("Failed to book appointment");
    } finally {
      setBooking(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#0a0a0a]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate("/dashboard")}
                className="text-slate-400 hover:text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <Stethoscope className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-white">Consult a Doctor</h1>
                  <p className="text-xs text-slate-500">Find specialists near you</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Consultation Type Toggle */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={consultationType === "in_person" ? "default" : "outline"}
            onClick={() => setConsultationType("in_person")}
            className={consultationType === "in_person" 
              ? "bg-emerald-600 hover:bg-emerald-700" 
              : "border-slate-700 text-slate-300 hover:bg-slate-800"}
          >
            <Building2 className="w-4 h-4 mr-2" />
            In-Person
          </Button>
          <Button
            variant={consultationType === "teleconsult" ? "default" : "outline"}
            onClick={() => setConsultationType("teleconsult")}
            className={consultationType === "teleconsult" 
              ? "bg-emerald-600 hover:bg-emerald-700" 
              : "border-slate-700 text-slate-300 hover:bg-slate-800"}
          >
            <Video className="w-4 h-4 mr-2" />
            Teleconsult
          </Button>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <Input
              placeholder="Search by name, specialty, or hospital..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 h-12"
            />
          </div>
          <Select value={selectedSpecialty} onValueChange={setSelectedSpecialty}>
            <SelectTrigger className="w-full sm:w-[200px] bg-slate-900/50 border-slate-700 text-white h-12">
              <SelectValue placeholder="All Specialties" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="all" className="text-white">All Specialties</SelectItem>
              {specialties.map(specialty => (
                <SelectItem key={specialty} value={specialty} className="text-white">
                  {specialty}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Doctor Cards */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
          </div>
        ) : filteredDoctors.length === 0 ? (
          <div className="text-center py-20">
            <Stethoscope className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No doctors found matching your criteria</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDoctors.map((doctor) => (
              <div
                key={doctor.id}
                className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5 hover:border-emerald-500/30 transition-all duration-300"
              >
                {/* Doctor Info */}
                <div className="flex items-start gap-4 mb-4">
                  <img
                    src={doctor.image}
                    alt={doctor.name}
                    className="w-16 h-16 rounded-xl object-cover bg-slate-800"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold text-lg truncate">{doctor.name}</h3>
                    <p className="text-emerald-400 text-sm font-medium">{doctor.specialty}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      <span className="text-white text-sm">{doctor.rating}</span>
                      <span className="text-slate-500 text-sm">({doctor.reviews} reviews)</span>
                    </div>
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-slate-400 text-sm">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{doctor.hospital}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-400 text-sm">
                    <Clock className="w-4 h-4 flex-shrink-0" />
                    <span>{doctor.experience}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-400 text-sm">
                    <GraduationCap className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{doctor.education}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-400 text-sm">
                    <Languages className="w-4 h-4 flex-shrink-0" />
                    <span>{doctor.languages.join(", ")}</span>
                  </div>
                </div>

                {/* Badges */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {doctor.teleconsult_available && (
                    <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                      <Video className="w-3 h-3 mr-1" />
                      Teleconsult
                    </Badge>
                  )}
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                    {doctor.fee}
                  </Badge>
                </div>

                {/* Book Button */}
                <Button
                  onClick={() => handleBookAppointment(doctor)}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Book Appointment
                </Button>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Booking Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Book Appointment</DialogTitle>
          </DialogHeader>
          
          {selectedDoctor && (
            <div className="space-y-6">
              {/* Doctor Summary */}
              <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
                <img
                  src={selectedDoctor.image}
                  alt={selectedDoctor.name}
                  className="w-12 h-12 rounded-lg object-cover"
                />
                <div>
                  <p className="text-white font-medium">{selectedDoctor.name}</p>
                  <p className="text-emerald-400 text-sm">{selectedDoctor.specialty}</p>
                </div>
              </div>

              {/* Form */}
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">Full Name *</Label>
                  <Input
                    value={bookingForm.name}
                    onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })}
                    placeholder="Enter your full name"
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Age *</Label>
                    <Input
                      type="number"
                      value={bookingForm.age}
                      onChange={(e) => setBookingForm({ ...bookingForm, age: e.target.value })}
                      placeholder="Age"
                      className="bg-slate-800 border-slate-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Sex *</Label>
                    <Select 
                      value={bookingForm.sex} 
                      onValueChange={(v) => setBookingForm({ ...bookingForm, sex: v })}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="Male" className="text-white">Male</SelectItem>
                        <SelectItem value="Female" className="text-white">Female</SelectItem>
                        <SelectItem value="Other" className="text-white">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-slate-300">Phone Number *</Label>
                  <Input
                    type="tel"
                    value={bookingForm.phone}
                    onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })}
                    placeholder="Enter phone number"
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                </div>

                <div>
                  <Label className="text-slate-300">Preferred Time Slot</Label>
                  <Select 
                    value={bookingForm.preferred_slot} 
                    onValueChange={(v) => setBookingForm({ ...bookingForm, preferred_slot: v })}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                      <SelectValue placeholder="Select a time slot" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {selectedDoctor.available_slots?.map(slot => (
                        <SelectItem key={slot} value={slot} className="text-white">{slot}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-slate-300">Complaints / Symptoms</Label>
                  <Input
                    value={bookingForm.complaints}
                    onChange={(e) => setBookingForm({ ...bookingForm, complaints: e.target.value })}
                    placeholder="Describe your symptoms"
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button
                onClick={handleSubmitBooking}
                disabled={booking}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                {booking ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Booking...
                  </>
                ) : (
                  "Confirm Booking"
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConsultDoctor;
